import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import {Check, X} from 'lucide-react';
import { lumi } from './lib/lumi';
import { ThankYou } from './components/ThankYou';
import { MentalControlCategory } from './components/MentalControlCategory';
import { MentalControlOverthinkingHub } from './components/MentalControlOverthinkingHub';
import { BlogArticleDemo } from './components/BlogArticleDemo';
import { WhySmartPeopleOverthink } from './components/WhySmartPeopleOverthink';
import { HowToStopReplayingConversations } from './components/HowToStopReplayingConversations';
import { MentalBoundaries } from './components/MentalBoundaries';
import { RuminationVsReflection } from './components/RuminationVsReflection';
import { DecisionFatigue } from './components/DecisionFatigue';
import { MeditationAnalyticalThinkers } from './components/MeditationAnalyticalThinkers';
import { AIAnxiety } from './components/AIAnxiety';
import { BrainAtNight } from './components/BrainAtNight';
import { MentalLoopsAtWork } from './components/MentalLoopsAtWork';
import { MentalContainmentSystem } from './components/MentalContainmentSystem';
import { ClarityVsCalm } from './components/ClarityVsCalm';
import { OverthinkingUnderPressure } from './components/OverthinkingUnderPressure';

function LandingPage() {
  const [isSticky, setIsSticky] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track ViewContent event on page load
  useEffect(() => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', {
        content_name: 'The Overthinking Reset',
        content_category: 'Ebook',
        content_type: 'product',
        value: 17.00,
        currency: 'USD'
      });
    }
  }, []);

  const scrollToCheckout = () => {
    document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCheckout = async () => {
    setIsLoading(true);

    // Track InitiateCheckout event
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'The Overthinking Reset',
        content_category: 'Ebook',
        content_type: 'product',
        value: 17.00,
        currency: 'USD'
      });
    }

    try {
      const result = await lumi.functions.invoke('createCheckout', {
        method: 'POST',
        body: {
          priceId: 'price_1SlEFLGXTAlsTOtimmwC7ELg',
          successUrl: `${window.location.origin}/success`,
          cancelUrl: window.location.origin,
          couponCode: couponCode || undefined
        }
      });

      if (result.url) {
        const win = window.open();
        if (win) {
          win.location.href = result.url;
        }
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMsg = error?.data?.error || error?.data?.details || 'Error processing payment. Please try again.';
      alert(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Amazon Badge - Sticky on scroll */}
      {isSticky && (
        <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-md z-50 animate-fade-in">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3">
            <span className="text-sm text-gray-600">Also available on</span>
            <a 
              href="https://www.amazon.com/dp/B0GM5RZS5L" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <svg className="w-16 h-5" viewBox="0 0 603 182" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M374.3 138.7c-42.4 31.3-103.8 47.9-156.7 47.9-74.1 0-140.9-27.4-191.5-73-4-3.6-.4-8.4 4.4-5.6 54.8 31.9 122.5 51 192.5 51 47.2 0 99.1-9.8 146.9-30 5.8-2.5 10.7 3.8 5 10.7z" fill="#FF9900"/>
                <path d="M388.8 122.3c-5.4-6.9-35.8-3.3-49.5-1.7-4.2.5-4.8-3.1-1.1-5.8 24.2-17 63.9-12.1 68.5-6.4 4.6 5.7-1.2 45.3-23.8 64.2-3.5 2.9-6.8 1.4-5.3-2.5 5.1-12.8 16.6-41.5 11.2-48z" fill="#FF9900"/>
                <path d="M348.5 32.3v-8.6c0-1.3.9-2.1 2.1-2.1h37.8c1.2 0 2.1.9 2.1 2.1v7.4c0 1.2-.9 2.8-2.6 5.3l-19.6 28c7.3-.2 15 .9 21.6 4.5 1.5.8 1.9 2 2 3.2v9.2c0 1.3-1.4 2.7-2.9 2-9.5-5-22.1-5.5-32.6.1-1.4.8-2.8-.7-2.8-2v-8.7c0-1.4 0-3.8 1.4-5.9l22.7-32.5h-19.7c-1.2 0-2.1-.9-2.1-2.1zm-64.8 62.1h-11.7c-1.1-.1-2-.8-2.1-1.9V24.1c0-1.1.9-2 2.1-2h10.9c1.1.1 2 .9 2.1 1.9v8.4h.2c2.8-8 8.1-11.6 15.2-11.6 7.2 0 11.7 3.6 14.9 11.6 2.8-8 9.2-11.6 16-11.6 4.9 0 10.2 2 13.5 6.5 3.6 4.9 2.9 12.1 2.9 18.4l-.1 47.1c0 1.1-.9 2-2.1 2h-11.7c-1.1-.1-2-.9-2.1-2V54.7c0-2.5.2-8.7-.3-11.1-.9-4-3.5-5.1-6.9-5.1-2.8 0-5.8 1.9-7 4.9-1.2 3-1.1 8-1.1 11.3v39.6c0 1.1-.9 2-2.1 2H300c-1.1-.1-2-.9-2.1-2l-.1-39.6c0-8.3 1.4-20.6-7.2-20.6-8.7 0-8.4 10-8.4 20.6v39.6c0 1.1-.9 2-2.1 2zm181.3-72.5c17.4 0 26.8 14.9 26.8 33.9 0 18.3-10.4 32.9-26.8 32.9-17.1 0-26.4-14.9-26.4-33.5 0-18.7 9.5-33.3 26.4-33.3zm0 12.2c-8.7 0-9.2 11.8-9.2 19.2 0 7.4-.1 23.1 9.1 23.1 9.1 0 9.5-12.7 9.5-20.4 0-5.1-.2-11.2-1.4-16.1-1-4.2-3-5.8-6.8-5.8h-.2zm33.4 60.3h-11.7c-1.1-.1-2-.9-2.1-2V24.1c0-1.1.9-2 2.1-2h10.9c1 .1 1.8.8 2 1.8v9.8h.2c3.1-8.7 7.5-13 16.2-13 5.3 0 10.5 1.9 13.9 7 3.1 4.7 3.1 12.5 3.1 18.2v47.5c0 1.1-.9 2-2.1 2h-11.7c-1-.1-1.9-.9-2-1.9V56.4c0-8.3 1-20.6-9.3-20.6-3.6 0-6.9 2.4-8.5 6.1-2.1 4.7-2.4 9.4-2.4 14.5v37.9c-.1 1.2-1 2.1-2.2 2.1h.6zM93.5 58.7c0 6.5.2 11.9-3.1 17.7-2.7 4.7-7 7.6-11.8 7.6-6.5 0-10.3-5-10.3-12.3 0-14.5 13-17.1 25.2-17.1v4.1zm11.8 28.4c-.8.7-1.9.7-2.8.3-3.9-3.2-4.6-4.7-6.7-7.8-6.4 6.5-11 8.5-19.3 8.5-9.9 0-17.5-6.1-17.5-18.3 0-9.5 5.2-16 12.5-19.2 6.4-2.8 15.3-3.3 22.1-4.1v-1.5c0-2.8.2-6.1-1.4-8.5-1.4-2.2-4.1-3.1-6.5-3.1-4.4 0-8.3 2.3-9.3 7-.2 1-.9 2-1.9 2l-11.3-1.2c-.9-.2-1.9-1-1.6-2.4 2.4-12.6 13.8-16.4 24-16.4 5.2 0 12 1.4 16.1 5.4 5.2 4.9 4.7 11.4 4.7 18.5v16.8c0 5 2.1 7.2 4.1 10 .7 1 .9 2.2-.1 2.9l-9.7 8.3c-.3.2-.6.3-.9.3-.4 0-.8-.2-1.2-.5h.7zm-67.5.2c-.8.7-1.9.7-2.8.3-3.9-3.2-4.6-4.7-6.7-7.8-6.4 6.5-11 8.5-19.3 8.5C-1 88.3-8.6 82.2-8.6 70c0-9.5 5.2-16 12.5-19.2C10.3 48 19.2 47.5 26 46.7v-1.5c0-2.8.2-6.1-1.4-8.5-1.4-2.2-4.1-3.1-6.5-3.1-4.4 0-8.3 2.3-9.3 7-.2 1-.9 2-1.9 2L-5.4 41.4c-.9-.2-1.9-1-1.6-2.4 2.4-12.6 13.8-16.4 24-16.4 5.2 0 12 1.4 16.1 5.4 5.2 4.9 4.7 11.4 4.7 18.5v16.8c0 5 2.1 7.2 4.1 10 .7 1 .9 2.2-.1 2.9l-9.7 8.3c-.3.2-.6.3-.9.3-.4 0-.8-.2-1.2-.5h.7z" fill="#221F1F"/>
              </svg>
            </a>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white md:py-32 pt-[40px] pl-[24px] pr-[24px] pb-[20px]">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 text-gray-900">Control Your Thinking — Without Fighting Your Mind

          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-4 leading-relaxed">A practical mental control system for people whose minds don’t shut off — and don’t want them to.

          </p>
          <p className="text-lg text-gray-500 mb-[16px]">No therapy talk. No meditation. Just structured mental control for high-functioning minds.

          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 mb-10 text-gray-600 text-sm md:text-base max-w-3xl mx-auto px-4">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#2C5F7D] rounded-full"></span>
              Stop mental loops before they drain your energy
            </span>
            <span className="hidden md:inline text-gray-300">•</span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#2C5F7D] rounded-full"></span>
              Make decisions without replaying everything
            </span>
            <span className="hidden md:inline text-gray-300">•</span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#2C5F7D] rounded-full"></span>
              Built for active, high-performing minds — not passive calm
            </span>
          </div>
          <button
            onClick={scrollToCheckout}
            className="inline-block px-10 py-5 bg-[#2C5F7D] text-white text-lg font-medium rounded-lg hover:bg-[#234a5f] transition-colors shadow-md">Get Instant Access


          </button>
        </div>
      </section>

      {/* Problem Awareness Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 pt-[60px] pl-[24px] pr-[24px] pb-[80px]">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight text-gray-900 text-center">
              Your mind doesn't stop because it's broken.
            </h2>
            
            <div className="space-y-6 max-w-3xl mx-auto">
              <div className="space-y-2 text-center">
                <p className="text-xl font-medium text-gray-900 text-left">Overthinking isn't emotional chaos.

                </p>
                <p className="text-xl text-gray-700 text-left">It's mental energy with no boundary.

                </p>
              </div>

              <div className="border-l-4 border-[#2C5F7D] pl-6 space-y-3 text-lg text-gray-700 leading-relaxed">
                <p>You replay conversations.</p>
                <p>You re-evaluate decisions that are already made.</p>
                <p>You think about things you can't act on — again and again.</p>
              </div>

              <div className="space-y-2 text-lg text-gray-700 text-center">
                <p className="text-left">Not because you want to.</p>
                <p className="font-medium text-gray-900 text-left">But because your mind never learned when to stop.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reframe Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            Overthinking Isn't a Weakness
          </h2>
          <div className="space-y-6 text-lg leading-relaxed text-gray-700">
            <p>
              Most people who overthink are capable, intelligent, and alert.
            </p>
            <p>
              Their minds are built to solve problems.
            </p>
            <p className="font-medium text-gray-900">
              The issue is simple:<br />
              modern problems don't always have clear endpoints.
            </p>
            <p>
              So the thinking keeps running.
            </p>
            <p className="italic">
              Not because something is wrong —<br />
              but because there's no system to contain it.
            </p>
          </div>
        </div>
      </section>

      {/* What This Book Is Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Title and subtitle above columns */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              The Overthinking Reset — Complete Digital System
            </h2>
            <p className="text-xl text-gray-600 mb-3 leading-relaxed">
              A practical guide designed to help you regain control of your thinking — without trying to silence it.
            </p>
            <p className="text-lg text-[#2C5F7D] font-medium">
              Instant download. Apply the same day.
            </p>
          </div>

          {/* Two column layout */}
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left column - Image */}
            <div className="flex justify-center md:justify-end">
              <img
                src="https://static.lumi.new/material/6c/6c05a211929862748872639e568c1478.png"
                alt="The Overthinking Reset Ebook"
                className="max-w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: '600px' }} />

            </div>

            {/* Right column - Content */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Inside the ebook, you'll get:</h3>
                
                <div className="space-y-5">
                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                      <p className="text-lg font-medium text-gray-900">A clear mental control framework</p>
                    </div>
                    <p className="text-base text-gray-600 ml-8">
                      Understand why overthinking doesn't stop — and where to interrupt it.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                      <p className="text-lg font-medium text-gray-900">The 3×3×3 Method</p>
                    </div>
                    <p className="text-base text-gray-600 ml-8">
                      A simple, repeatable process to break mental loops before they escalate.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                      <p className="text-lg font-medium text-gray-900">Real-time interruption techniques</p>
                    </div>
                    <p className="text-base text-gray-600 ml-8">
                      Stop unproductive thinking while it's happening, not after damage is done.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                      <p className="text-lg font-medium text-gray-900">Mental boundaries you can reuse daily</p>
                    </div>
                    <p className="text-base text-gray-600 ml-8">
                      Clear stopping points so your mind knows when to disengage.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                      <p className="text-lg font-medium text-gray-900">A structure that works under pressure</p>
                    </div>
                    <p className="text-base text-gray-600 ml-8">
                      No rituals, no routines that collapse when life gets busy.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Full width two-column section below book cover */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mt-12 pt-12 border-t border-gray-200">
            {/* Left column - Designed for */}
            <div className="space-y-6 text-center">
              <p className="text-lg font-medium text-gray-900 text-center">Designed for people who think in systems</p>
              <div className="space-y-2 text-base text-gray-600 ml-4">
                <p>• High-functioning adults</p>
                <p>• Analytical, problem-solving minds</p>
                <p>• People who want control, not calm</p>
              </div>
            </div>

            {/* Right column - Format */}
            <div className="space-y-4 text-center">
              <p className="text-lg font-medium text-gray-900">Format</p>
              <div className="space-y-2 text-base text-gray-600">
                <p>Digital ebook (instant access)</p>
                <p>Read on any device</p>
                <p>One-time purchase</p>
                <p>No subscription</p>
              </div>
            </div>
          </div>

          {/* Quote section */}
          <div className="mt-16 text-center">
            <blockquote className="text-2xl font-medium text-gray-900 italic border-l-4 border-[#2C5F7D] pl-6 inline-block text-left">
              This isn't motivation.<br />
              It's a usable mental structure.
            </blockquote>
          </div>
        </div>
      </section>

      {/* How the system works Section */}
      <section className="bg-white pt-[60px] pl-[24px] pr-[24px] pb-[60px]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            How the system works
          </h2>
          
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-lg text-gray-700">A simple framework to identify unproductive loops</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-lg text-gray-700">The 3×3×3 Method to stop overthinking before it escalates</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-lg text-gray-700">Clear mental boundaries you can apply immediately</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-lg text-gray-700">A daily structure that works even under stress</p>
            </div>
          </div>
          
          <div className="mt-10 pt-8 border-t border-gray-200 space-y-2 text-center">
            <p className="text-lg text-gray-700 mb-4">For example: when a thought loop starts, you apply X instead of replaying it.</p>
            <p className="text-lg text-gray-600 italic">No rituals.</p>
            <p className="text-lg text-gray-600 italic">No motivation required.</p>
            <p className="text-xl text-gray-900 font-bold">Just structure.</p>
          </div>
        </div>
      </section>

      {/* What changes when you apply it Section */}
      <section className="bg-gray-50 pt-[60px] pl-[24px] pr-[24px] pb-[60px]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            What changes when you apply it
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <Check className="w-6 h-6 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
              <p className="text-lg text-gray-700">Fewer repeated thoughts</p>
            </div>
            <div className="flex items-start gap-4">
              <Check className="w-6 h-6 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
              <p className="text-lg text-gray-700">Faster decisions</p>
            </div>
            <div className="flex items-start gap-4">
              <Check className="w-6 h-6 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
              <p className="text-lg text-gray-700">Less mental fatigue at the end of the day</p>
            </div>
            <div className="flex items-start gap-4">
              <Check className="w-6 h-6 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
              <p className="text-lg text-gray-700">More usable energy for work and life</p>
            </div>
          </div>
          
          <div className="border-t border-gray-200 text-center space-y-1 mt-[16px] pt-[16px]">
            <p className="text-xl text-gray-900 leading-relaxed text-left">
              Your mind stays active —<br />
              but no longer wasteful.
            </p>
          </div>
        </div>
      </section>

      {/* Who This Is For / Not For Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">
            Is This For You?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* This Is For */}
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">This is for you if:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">Your mind stays "on" even when you're tired</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">You've tried calming techniques that didn't stick</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">You value clarity more than comfort</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">You want a system, not motivation</p>
                </div>
              </div>
            </div>

            {/* This Is NOT For */}
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">This is NOT for you if:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">If you're looking for emotional reassurance</p>
                </div>
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">If you want your mind to be quiet all the time</p>
                </div>
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-lg text-gray-700">If you expect instant peace without effort</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="space-y-4 text-lg text-gray-700">
            <p className="font-medium">Designed for high-functioning adults</p>
            <p>Built from practical mental control frameworks</p>
            <p>Created for people who think in systems and need structure</p>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            Risk-Free Guarantee
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            7-day no-questions-asked refund. If this system doesn't work for you, you get your money back.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="checkout" className="bg-white pt-[80px] pl-[24px] pr-[24px] pb-[20px]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            The Overthinking Reset
          </h2>
          <p className="text-xl text-gray-600 mb-6 leading-relaxed">
            A practical mental control system for people who think deeply — and want structure, not silence.
          </p>
          <p className="text-base text-gray-700 mb-8 leading-relaxed">
            This is a short, focused ebook designed to be read and applied immediately.<br />
            You'll learn how to interrupt mental loops, set clear boundaries for thought, and redirect mental energy instead of wasting it.
          </p>

          <div className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-8">
            <h3 className="text-2xl font-bold mb-6 text-gray-900">What you're getting</h3>
            
            <div className="space-y-4 text-left mb-8">
              <p className="flex items-start gap-3 text-base text-gray-700">
                <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                The complete Overthinking Reset ebook (instant digital download)
              </p>
              <p className="flex items-start gap-3 text-base text-gray-700">
                <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                A clear, repeatable mental control framework
              </p>
              <p className="flex items-start gap-3 text-base text-gray-700">
                <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                The 3×3×3 Method to stop overthinking before it escalates
              </p>
              <p className="flex items-start gap-3 text-base text-gray-700">
                <Check className="w-5 h-5 text-[#2C5F7D] flex-shrink-0 mt-0.5" />
                Practical tools you can use in real time, even under pressure
              </p>
            </div>

            <div className="text-center space-y-1 mb-8 py-6 border-t border-b border-gray-200">
              <p className="text-base text-gray-600">No motivation.</p>
              <p className="text-base text-gray-600">No rituals.</p>
              <p className="text-lg font-medium text-gray-900">Just structure you can reuse whenever overthinking returns.</p>
            </div>

            <div className="mb-8">
              <p className="text-lg text-gray-600 mb-2">One-time purchase</p>
              <p className="text-5xl font-bold text-gray-900 mb-6">$17</p>
              
              <div className="space-y-2 text-base text-gray-700">
                <p>Instant digital access</p>
                <p>Read on any device</p>
                <p>No subscription</p>
                <p>Secure checkout via Stripe</p>
                <p>7-day no-questions-asked refund</p>
              </div>

              <p className="text-base text-gray-600 mt-6 italic">
                Less than a single therapy session.<br />
                Built to be applied for years.
              </p>
            </div>

            {isPurchased ?
            <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <p className="text-lg font-medium text-green-900 mb-2">Purchase Successful</p>
                  <p className="text-green-700 mb-4">Your ebook is ready to download.</p>
                  {downloadUrl &&
                <a
                  href={downloadUrl}
                  download
                  className="inline-block px-8 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors">

                      Download Your Ebook
                    </a>
                }
                </div>
              </div> :

            <div className="space-y-3">
                {/* Coupon field - hidden by default */}
                <div className="text-left">
                  




                  {showCoupon &&
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter coupon code"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7D] focus:border-transparent" />

                }
                </div>
                <button
                onClick={handleCheckout}
                disabled={isLoading}
                className="w-full px-10 py-5 bg-[#2C5F7D] text-white text-lg font-medium rounded-lg hover:bg-[#234a5f] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed">

                  {isLoading ? 'Processing...' : 'Download the Ebook Instantly'}
                </button>
                <p className="text-sm text-gray-500">
                  Secure payment processing by Stripe. Your information is protected.
                </p>
              </div>
            }
          </div>

          <div className="text-sm text-gray-600 space-y-2 leading-relaxed">
            
            
            <div className="space-y-1 mt-3">
              
              
            </div>
          </div>
        </div>
      </section>

      {/* Also Available on Amazon Section */}
      <section className="bg-white py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-600 mb-4">Also available on</p>
          <div className="flex justify-center items-center gap-4 mb-4">
            <img 
              src="https://static.lumi.new/material/2a/2aee22cba25fc9939900ac4e91fa747b.png" 
              alt="Amazon" 
              className="w-24 h-8 object-contain" 
            />
            <svg className="w-24 h-8" viewBox="0 0 603 182" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
              <path d="M374.3 138.7c-42.4 31.3-103.8 47.9-156.7 47.9-74.1 0-140.9-27.4-191.5-73-4-3.6-.4-8.4 4.4-5.6 54.8 31.9 122.5 51 192.5 51 47.2 0 99.1-9.8 146.9-30 5.8-2.5 10.7 3.8 5 10.7z" fill="#FF9900"/>
              <path d="M388.8 122.3c-5.4-6.9-35.8-3.3-49.5-1.7-4.2.5-4.8-3.1-1.1-5.8 24.2-17 63.9-12.1 68.5-6.4 4.6 5.7-1.2 45.3-23.8 64.2-3.5 2.9-6.8 1.4-5.3-2.5 5.1-12.8 16.6-41.5 11.2-48z" fill="#FF9900"/>
              <path d="M348.5 32.3v-8.6c0-1.3.9-2.1 2.1-2.1h37.8c1.2 0 2.1.9 2.1 2.1v7.4c0 1.2-.9 2.8-2.6 5.3l-19.6 28c7.3-.2 15 .9 21.6 4.5 1.5.8 1.9 2 2 3.2v9.2c0 1.3-1.4 2.7-2.9 2-9.5-5-22.1-5.5-32.6.1-1.4.8-2.8-.7-2.8-2v-8.7c0-1.4 0-3.8 1.4-5.9l22.7-32.5h-19.7c-1.2 0-2.1-.9-2.1-2.1zm-64.8 62.1h-11.7c-1.1-.1-2-.8-2.1-1.9V24.1c0-1.1.9-2 2.1-2h10.9c1.1.1 2 .9 2.1 1.9v8.4h.2c2.8-8 8.1-11.6 15.2-11.6 7.2 0 11.7 3.6 14.9 11.6 2.8-8 9.2-11.6 16-11.6 4.9 0 10.2 2 13.5 6.5 3.6 4.9 2.9 12.1 2.9 18.4l-.1 47.1c0 1.1-.9 2-2.1 2h-11.7c-1.1-.1-2-.9-2.1-2V54.7c0-2.5.2-8.7-.3-11.1-.9-4-3.5-5.1-6.9-5.1-2.8 0-5.8 1.9-7 4.9-1.2 3-1.1 8-1.1 11.3v39.6c0 1.1-.9 2-2.1 2H300c-1.1-.1-2-.9-2.1-2l-.1-39.6c0-8.3 1.4-20.6-7.2-20.6-8.7 0-8.4 10-8.4 20.6v39.6c0 1.1-.9 2-2.1 2zm181.3-72.5c17.4 0 26.8 14.9 26.8 33.9 0 18.3-10.4 32.9-26.8 32.9-17.1 0-26.4-14.9-26.4-33.5 0-18.7 9.5-33.3 26.4-33.3zm0 12.2c-8.7 0-9.2 11.8-9.2 19.2 0 7.4-.1 23.1 9.1 23.1 9.1 0 9.5-12.7 9.5-20.4 0-5.1-.2-11.2-1.4-16.1-1-4.2-3-5.8-6.8-5.8h-.2zm33.4 60.3h-11.7c-1.1-.1-2-.9-2.1-2V24.1c0-1.1.9-2 2.1-2h10.9c1 .1 1.8.8 2 1.8v9.8h.2c3.1-8.7 7.5-13 16.2-13 5.3 0 10.5 1.9 13.9 7 3.1 4.7 3.1 12.5 3.1 18.2v47.5c0 1.1-.9 2-2.1 2h-11.7c-1-.1-1.9-.9-2-1.9V56.4c0-8.3 1-20.6-9.3-20.6-3.6 0-6.9 2.4-8.5 6.1-2.1 4.7-2.4 9.4-2.4 14.5v37.9c-.1 1.2-1 2.1-2.2 2.1h.6zM93.5 58.7c0 6.5.2 11.9-3.1 17.7-2.7 4.7-7 7.6-11.8 7.6-6.5 0-10.3-5-10.3-12.3 0-14.5 13-17.1 25.2-17.1v4.1zm11.8 28.4c-.8.7-1.9.7-2.8.3-3.9-3.2-4.6-4.7-6.7-7.8-6.4 6.5-11 8.5-19.3 8.5-9.9 0-17.5-6.1-17.5-18.3 0-9.5 5.2-16 12.5-19.2 6.4-2.8 15.3-3.3 22.1-4.1v-1.5c0-2.8.2-6.1-1.4-8.5-1.4-2.2-4.1-3.1-6.5-3.1-4.4 0-8.3 2.3-9.3 7-.2 1-.9 2-1.9 2l-11.3-1.2c-.9-.2-1.9-1-1.6-2.4 2.4-12.6 13.8-16.4 24-16.4 5.2 0 12 1.4 16.1 5.4 5.2 4.9 4.7 11.4 4.7 18.5v16.8c0 5 2.1 7.2 4.1 10 .7 1 .9 2.2-.1 2.9l-9.7 8.3c-.3.2-.6.3-.9.3-.4 0-.8-.2-1.2-.5h.7zm-67.5.2c-.8.7-1.9.7-2.8.3-3.9-3.2-4.6-4.7-6.7-7.8-6.4 6.5-11 8.5-19.3 8.5C-1 88.3-8.6 82.2-8.6 70c0-9.5 5.2-16 12.5-19.2C10.3 48 19.2 47.5 26 46.7v-1.5c0-2.8.2-6.1-1.4-8.5-1.4-2.2-4.1-3.1-6.5-3.1-4.4 0-8.3 2.3-9.3 7-.2 1-.9 2-1.9 2L-4.4 41.4c-.9-.2-1.9-1-1.6-2.4 2.4-12.6 13.8-16.4 24-16.4 5.2 0 12 1.4 16.1 5.4 5.2 4.9 4.7 11.4 4.7 18.5v16.8c0 5 2.1 7.2 4.1 10 .7 1 .9 2.2-.1 2.9l-9.7 8.3c-.3.2-.6.3-.9.3-.4 0-.8-.2-1.2-.5h.7zm-18-45c-9.3 0-9.7 12.7-9.7 20.6 0 5.1.2 11.2 1.4 16.1 1 4.2 3.6 5.8 7.2 5.8 8.7 0 9.2-11.8 9.2-19.2 0-7.4.1-23.3-8.1-23.3z" fill="#221F1F"/>
            </svg>
          </div>
          <a 
            href="https://www.amazon.com/dp/B0GM5RZS5L" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:border-[#FF9900] hover:text-[#FF9900] transition-colors">
            View on Amazon
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* Guarantee / Risk Reduction Section */}
      <section className="bg-gray-50 pt-[20px] pl-[24px] pr-[24px] pb-[20px]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p className="font-medium text-gray-900">Try it. Apply it. Decide calmly.</p>
            <p>
              If it doesn't help you regain control of your thinking,<br />
              you're covered by a full refund.
            </p>
            <div className="space-y-2">
              <p className="text-gray-600">No pressure.</p>
              <p className="text-gray-600">No tricks.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-gray-500 italic">
            This book is not a substitute for professional psychological or medical advice.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            Stop wasting mental energy.
          </h2>
          




        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center text-gray-600">
          <p className="mb-2">The Overthinking Reset</p>
          <p className="text-sm">© 2024. All rights reserved.</p>
        </div>
      </footer>
    </div>);

}

function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Invalid Session</h1>
          <p className="text-gray-600 mb-8">No session ID found. Please try purchasing again.</p>
          <a href="/" className="inline-block px-8 py-3 bg-[#2C5F7D] text-white font-medium rounded-lg hover:bg-[#234a5f] transition-colors">
            Return to Home
          </a>
        </div>
      </div>);

  }

  return <ThankYou sessionId={sessionId} />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/mental-control" element={<MentalControlCategory />} />
        <Route path="/mental-control-overthinking" element={<MentalControlOverthinkingHub />} />
        <Route path="/article-template" element={<BlogArticleDemo />} />
        <Route path="/why-smart-people-overthink" element={<WhySmartPeopleOverthink />} />
        <Route path="/stop-replaying-conversations" element={<HowToStopReplayingConversations />} />
        <Route path="/mental-boundaries" element={<MentalBoundaries />} />
        <Route path="/rumination-vs-reflection" element={<RuminationVsReflection />} />
        <Route path="/decision-fatigue" element={<DecisionFatigue />} />
        <Route path="/meditation-analytical-thinkers" element={<MeditationAnalyticalThinkers />} />
        <Route path="/ai-anxiety" element={<AIAnxiety />} />
        <Route path="/brain-at-night" element={<BrainAtNight />} />
        <Route path="/mental-loops-at-work" element={<MentalLoopsAtWork />} />
        <Route path="/mental-containment-system" element={<MentalContainmentSystem />} />
        <Route path="/clarity-vs-calm" element={<ClarityVsCalm />} />
        <Route path="/overthinking-under-pressure" element={<OverthinkingUnderPressure />} />
      </Routes>
    </Router>);

}

export default App;